import { useState } from 'react';
import { FileDown } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useEmailContext } from 'features/email-generator/EmailGeneratorContext';
import { removeMarkdown } from 'lib/utils';

const ExportEmail = () => {
  const [open, setOpen] = useState(false);
  const { emailContent, activeIndex } = useEmailContext();
  const [format, setFormat] = useState('pdf');

  console.log(emailContent, activeIndex);

  const handleExport = async (selectedIndex: number) => {
    try {
      // Ensure emailContent exists
      if (!emailContent || emailContent.length === 0) {
        throw new Error('No email content available to export.');
      }

      const selectedEmail = removeMarkdown(emailContent[selectedIndex]);

      if (!selectedEmail) {
        throw new Error('No valid emails selected for export.');
      }

      // Generate printable HTML content
      const printStyles = `
        <style>
          @media print {
            @page {
              size: A4;
              margin: 20mm 15mm;
            }

            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            /* Typography */
            h1 {
              font-size: 24pt;
              margin-bottom: 15pt;
              page-break-after: avoid;
            }

            h2 {
              font-size: 20pt;
              margin-bottom: 12pt;
              page-break-after: avoid;
            }

            h3 {
              font-size: 16pt;
              margin-top: 20pt;
              margin-bottom: 10pt;
              page-break-after: avoid;
            }

            p {
              font-size: 12pt;
              line-height: 1.6;
              margin-bottom: 10pt;
              text-align: justify;
            }

            /* Images */
            img {
              max-width: 180mm;
              height: auto;
              margin: 10pt auto;
              display: block;
              page-break-inside: avoid;
            }

            /* Tables */
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15pt 0;
              page-break-inside: avoid;
            }

            th, td {
              padding: 8pt;
              border: 1pt solid #ddd;
            }

            /* Lists */
            ul, ol {
              margin: 10pt 0;
              padding-left: 20pt;
            }

            li {
              margin-bottom: 5pt;
            }

            /* Page break controls */
            .page-break {
              page-break-before: always;
            }

            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
            }

            img, table, figure {
              page-break-inside: avoid;
            }
          }
        </style>
        <style>
          * {
            font-family: Arial, system-ui, -apple-system, sans-serif;
            text-rendering: optimizeLegibility;
            -webkit-font-smoothing: antialiased;
          }

          .print-container {
            width: 210mm;
            margin: 0 auto;
            padding: 20mm 15mm;
            font-family: Arial, system-ui, -apple-system, sans-serif;
          }
        </style>
      `;

      const emailHtmlContent = `<div class="email-section">
            <h2>Email ${selectedIndex + 1}</h2>
            <p>${selectedEmail}</p>
          </div>
          <div class="page-break"></div>
        `;

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Preview</title>
            ${printStyles}
          </head>
          <body>
            <div class="print-container">
              ${emailHtmlContent}
            </div>
            <script>
              // Auto-print when the page loads
              window.onload = () => {
                setTimeout(() => {
                  window.print();
                }, 500);
              };
            </script>
          </body>
        </html>
      `;

      // Open new window for print preview
      const printWindow = window.open('', '_blank');
      if (!printWindow) throw new Error('Failed to open print window.');

      printWindow.document.write(printContent);
      printWindow.document.close();
    } catch (error) {
      console.error('PDF export failed:', error);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button
          variant="customSecondary"
          className="text-primary text-[14px] bg-secondary"
        >
          Export <FileDown size={16} />
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-background-overlay/50 z-20" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background-main rounded-lg shadow-lg p-6 w-full max-w-md flex flex-col gap-1 z-30 ">
          <Dialog.Title className="text-2xl text-left font-bold text-text-primary">
            Export Email
          </Dialog.Title>
          <div className="flex flex-col mt-2 w-full gap-4">
            <div className="flex flex-col">
              <label className="flex flex-col text-text-primary font-normal">
                Format
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="mt-2 bg-background text-text-primary">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent className="bg-background-main">
                    {/* <SelectItem value="txt">Plain Text (.txt)</SelectItem>
                    <SelectItem value="doc">Word Document (.doc)</SelectItem> */}
                    <SelectItem value="pdf">PDF Document (.pdf)</SelectItem>
                  </SelectContent>
                </Select>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <Button
                  variant="customSecondary"
                  className="text-primary bg-secondary"
                >
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={() => handleExport(activeIndex)}
                variant="customSecondary"
                className="text-primary bg-secondary"
              >
                Export
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExportEmail;
